// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type { SignedDocumentDTO } from '../types/signedDocument';
import type { PageResponse } from '../types/relationship';

/**
 * Client for the Phase 3 {@code /api/signed-documents} endpoint that
 * returns a merged, createdAt-DESC stream of LOAs and CoAs for the
 * "Client Signed Documents" tab on the relationship detail page.
 */
export const signedDocumentApi = {
  /**
   * Fetch a page of signed documents (LOAs + CoAs merged) for the given
   * profile. Backend sorts by {@code createdAt DESC} by default.
   */
  getSignedDocumentsByProfile: async (
    profileId: string,
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<SignedDocumentDTO>> => {
    const response = await api.get<PageResponse<SignedDocumentDTO>>(
      `/signed-documents/profile/${profileId}`,
      { params: { page, size } }
    );
    return response.data;
  },

  /**
   * Download any signed document by its storage path. Works for LOAs,
   * CoAs, CoA slices, and signing envelopes — the backend reads from
   * tenant document storage using the path directly.
   */
  /**
   * Fetch signed document slices (LOA + CoA) for a specific policy.
   */
  getSignedDocumentsByPolicy: async (
    policyId: string,
    page: number = 0,
    size: number = 20
  ): Promise<PageResponse<SignedDocumentDTO>> => {
    const response = await api.get<PageResponse<SignedDocumentDTO>>(
      `/signed-documents/policy/${policyId}`,
      { params: { page, size } }
    );
    return response.data;
  },

  download: async (signedDocumentUrl: string): Promise<Blob> => {
    // URL-safe base64 encode: replace +→-, /→_, strip = padding
    const encodedPath = btoa(signedDocumentUrl)
      .replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
    const response = await api.get('/signed-documents/download', {
      params: { path: encodedPath, encoded: 'true' },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Toggle whether a signed document is shown to the client. {@code docType} is
   * the row's discriminator ('LOA' | 'COA' | 'UPLOAD'); the backend routes
   * UPLOAD to the client-documents service and LOA/CoA to the signed-document
   * mutation service.
   */
  setVisibility: async (docType: string, id: string, visible: boolean): Promise<void> => {
    await api.patch(`/signed-documents/${docType}/${id}/visibility`, { visible });
  },

  /** Soft-delete a signed document (LOA / CoA / advisor upload) by type + id. */
  softDelete: async (docType: string, id: string): Promise<void> => {
    await api.delete(`/signed-documents/${docType}/${id}`);
  },
};
